import EventEmitter from "eventemitter3";
import { ITranscriptionService } from "../interfaces/ITranscriptionService";
import {
  BatchReprocessError,
  ResolvedWhisperConnectionContext,
} from "../batchReprocess";
import { DurableAudioSessionManager } from "../recovery";
import {
  createUnsupportedTelemetrySnapshot,
  TelemetryRow,
  TelemetrySnapshot,
  TranscriptionMetricsProvider,
} from "../metrics";

const REALTIME_AUDIO_PROCESSOR_NAME = "realtime-audio-processor";
const REALTIME_AUDIO_WORKLET_CODE = `
class RealtimeAudioProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.active = true;
    this.port.onmessage = (event) => {
      if (event?.data?.type === "sofya-stop") {
        this.active = false;
      }
    };
  }

  process(inputs, outputs, params) {
    if (!this.active) {
      return false;
    }

    const channel = inputs[0]?.[0];
    if (channel) {
      this.port.postMessage(channel);
    }

    return true;
  }
}

registerProcessor("${REALTIME_AUDIO_PROCESSOR_NAME}", RealtimeAudioProcessor);
`;

interface BatchTranscriptionConfig {
  language: string;
  translation_lang?: string;
  token?: string;
  headers?: Record<string, string>;
  batch?: {
    parseResponse?: (response: Response) => Promise<unknown>;
  };
  provider?: TranscriptionMetricsProvider;
  resolvedConnectionContext?: ResolvedWhisperConnectionContext;
}

type BatchResponseConfig = NonNullable<BatchTranscriptionConfig["batch"]>;

export class BatchTranscriptionAdapter
  extends EventEmitter
  implements ITranscriptionService
{
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private input: MediaStreamAudioSourceNode | undefined;
  private recordingNode: AudioWorkletNode | undefined;
  private readonly batchConfig?: BatchResponseConfig;
  private readonly provider: TranscriptionMetricsProvider;
  private stopInFlightPromise: Promise<unknown> | null = null;
  private recordState: "PAUSED" | "RECORDING" = "PAUSED";
  private durableAudioSession: DurableAudioSessionManager | null = null;
  public readonly resolvedConnectionContext?: ResolvedWhisperConnectionContext;

  constructor(config: BatchTranscriptionConfig) {
    super();
    this.batchConfig = config.batch;
    this.provider = config.provider ?? "sofya_as_service";
    this.resolvedConnectionContext = config.resolvedConnectionContext;
  }

  public async startTranscription(mediaStream: MediaStream): Promise<void> {
    if (!mediaStream) {
      throw new Error("No mediaStream was received.");
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("getUserMedia not supported on your browser!");
    }

    this.mediaStream = mediaStream;
    this.durableAudioSession = new DurableAudioSessionManager();
    await this.durableAudioSession.startSession();

    const audioContext = this.ensureAudioContext();
    if (
      typeof audioContext.resume === "function" &&
      audioContext.state !== "running"
    ) {
      await audioContext.resume();
    }

    this.input = audioContext.createMediaStreamSource(mediaStream);
    this.recordingNode = await this.setupRecordingWorkletNode();
    this.recordingNode.port.onmessage = (event: { data: Float32Array }) => {
      this.postMessage(event.data);
    };
    this.input.connect(this.recordingNode);
    this.recordState = "RECORDING";
  }

  public async stopTranscription(): Promise<unknown> {
    if (this.stopInFlightPromise) {
      return this.stopInFlightPromise;
    }

    this.stopInFlightPromise = this.performStopTranscription().finally(() => {
      this.stopInFlightPromise = null;
    });

    return this.stopInFlightPromise;
  }

  public pauseTranscription(): void {
    this.recordState = "PAUSED";
    this.input?.disconnect();
  }

  public resumeTranscription(): void {
    if (this.input && this.recordingNode) {
      this.input.connect(this.recordingNode);
    }
    this.recordState = "RECORDING";
    if (
      this.audioContext &&
      typeof this.audioContext.resume === "function" &&
      this.audioContext.state !== "running"
    ) {
      void this.audioContext.resume();
    }
  }

  public getResilienceStatus(): null {
    return null;
  }

  public getTelemetrySnapshot(): TelemetrySnapshot {
    return createUnsupportedTelemetrySnapshot(this.provider);
  }

  public getTelemetryRows(): TelemetryRow[] {
    return [];
  }

  public clearTelemetryRows(): void {}

  public resetTelemetry(): void {}

  private async performStopTranscription(): Promise<unknown> {
    if (!this.durableAudioSession) {
      throw new BatchReprocessError("AUDIO_NOT_AVAILABLE");
    }

    this.recordingNode?.port.postMessage({ type: "sofya-stop" });
    this.recordState = "PAUSED";
    this.input?.disconnect();
    this.stopMediaStreamTracks();
    await this.durableAudioSession.flush("stop");

    let output: Blob | ReadableStream<Uint8Array>;
    try {
      output = await this.durableAudioSession.buildFinalUpload("wav");
    } catch (error) {
      throw new BatchReprocessError("AUDIO_NOT_AVAILABLE", {
        cause: error,
        message: error instanceof Error ? error.message : undefined,
      });
    }

    const wavBlob =
      output instanceof Blob
        ? output
        : await this.readableStreamToBlob(output, "audio/wav");

    if (typeof fetch !== "function") {
      throw new BatchReprocessError("HTTP_ERROR", {
        message: "Primary batch mode requested but fetch API is unavailable.",
      });
    }

    const formData = new FormData();
    formData.append("file", wavBlob, "consultation.wav");

    try {
      const response = await fetch(this.buildBatchUrl(), {
        method: "POST",
        headers: this.buildBatchHeaders(),
        body: formData,
      });

      if (!response.ok) {
        throw new BatchReprocessError("HTTP_ERROR", {
          status: response.status,
          message: `Primary batch mode failed with status ${response.status}.`,
        });
      }

      const payload = this.batchConfig?.parseResponse
        ? await this.batchConfig.parseResponse(response)
        : await response.json();

      this.durableAudioSession.scheduleStorageDropAfterStop();
      await this.closeAudioContext();
      this.emit("stopped");
      return payload;
    } catch (error) {
      await this.closeAudioContext();
      if (error instanceof BatchReprocessError) {
        throw error;
      }

      if (error instanceof TypeError) {
        throw new BatchReprocessError("CORS_BLOCKED", { cause: error });
      }

      throw new BatchReprocessError("INVALID_RESPONSE", { cause: error });
    }
  }

  private ensureAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new AudioContext({
        sampleRate: 44100,
      });
    }

    return this.audioContext;
  }

  private async setupRecordingWorkletNode() {
    const audioContext = this.ensureAudioContext();
    await this.ensureWorkletModuleLoaded(audioContext);
    return new AudioWorkletNode(audioContext, REALTIME_AUDIO_PROCESSOR_NAME);
  }

  private async ensureWorkletModuleLoaded(audioContext: AudioContext) {
    const blob = new Blob([REALTIME_AUDIO_WORKLET_CODE], {
      type: "application/javascript",
    });
    const moduleUrl = URL.createObjectURL(blob);
    try {
      await audioContext.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }
  }

  private postMessage(sampleData: Float32Array) {
    if (this.recordState !== "RECORDING" || !this.durableAudioSession) {
      return;
    }

    const outputSampleRate = 16000;
    const decreaseResultBuffer = this.decreaseSampleRate(
      sampleData,
      44100,
      outputSampleRate
    );

    if (!decreaseResultBuffer) {
      return;
    }

    const audioPayload = this.convertFloat32ToInt16(decreaseResultBuffer);
    const durationMs = this.getAudioPayloadDurationMs(audioPayload.byteLength);
    void this.durableAudioSession.appendCaptureChunk(audioPayload, durationMs);
  }

  private decreaseSampleRate(
    buffer: Float32Array,
    inputSampleRate: number,
    outputSampleRate: number
  ) {
    if (outputSampleRate === inputSampleRate) {
      return buffer;
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.ceil(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0;
      let count = 0;
      for (
        let index = offsetBuffer;
        index < nextOffsetBuffer && index < buffer.length;
        index += 1
      ) {
        accum += buffer[index];
        count += 1;
      }
      result[offsetResult] = accum / count;
      offsetResult += 1;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  private convertFloat32ToInt16(buffer: Float32Array) {
    let length = buffer.length;
    const convertedBuffer = new Int16Array(length);
    while (length--) {
      convertedBuffer[length] = Math.min(1, buffer[length]) * 0x7fff;
    }
    return convertedBuffer.buffer;
  }

  private getAudioPayloadDurationMs(byteLength: number) {
    const bytesPerMs = (16000 * 2) / 1000;
    return byteLength / bytesPerMs;
  }

  private async readableStreamToBlob(
    stream: ReadableStream<Uint8Array>,
    contentType: string
  ): Promise<Blob> {
    const reader = stream.getReader();
    const chunks: ArrayBuffer[] = [];

    while (true) {
      const result = await reader.read();
      if (result.done) {
        break;
      }

      const chunk = result.value;
      const copy = new Uint8Array(chunk.byteLength);
      copy.set(chunk);
      chunks.push(copy.buffer);
    }

    return new Blob(chunks, { type: contentType });
  }

  private buildBatchUrl() {
    const baseUrl =
      this.resolvedConnectionContext?.batchBaseUrl ??
      (typeof window !== "undefined" && window.location?.href
        ? window.location.href
        : "http://localhost/");
    const endpoint =
      this.resolvedConnectionContext?.batchDefaultUrl ??
      this.resolvedConnectionContext?.realtimeUrl;

    if (!endpoint) {
      throw new BatchReprocessError("NOT_CONFIGURED");
    }

    const batchUrl = new URL(endpoint, baseUrl);

    if (!this.resolvedConnectionContext?.realtimeUrl) {
      return batchUrl.toString();
    }

    const realtimeUrl = new URL(this.resolvedConnectionContext.realtimeUrl, baseUrl);
    realtimeUrl.searchParams.forEach((value, key) => {
      if (!batchUrl.searchParams.has(key)) {
        batchUrl.searchParams.set(key, value);
      }
    });

    return batchUrl.toString();
  }

  private buildBatchHeaders(): Record<string, string> | undefined {
    const headers = new Headers();

    Object.entries(this.resolvedConnectionContext?.headers ?? {}).forEach(
      ([key, value]) => {
        if (typeof value === "string" && value.length > 0) {
          headers.set(key, value);
        }
      }
    );

    headers.delete("Content-Type");

    if (
      this.resolvedConnectionContext?.authToken &&
      !headers.has("Authorization")
    ) {
      headers.set(
        "Authorization",
        `Bearer ${this.resolvedConnectionContext.authToken}`
      );
    }

    const output = Object.fromEntries(headers.entries());
    return Object.keys(output).length > 0 ? output : undefined;
  }

  private stopMediaStreamTracks() {
    this.mediaStream?.getTracks?.().forEach((track) => {
      if (typeof track.stop === "function") {
        track.stop();
      }
    });
    this.mediaStream = null;
  }

  private async closeAudioContext() {
    this.recordingNode = undefined;
    this.input = undefined;

    if (!this.audioContext) {
      return;
    }

    if (typeof this.audioContext.close === "function") {
      await this.audioContext.close();
    }

    this.audioContext = null;
  }
}
